/**
 * <pdp-reviews-modal>
 *
 * Wraps the PDP "Write a review" flow. Intercepts clicks on WiseReviews'
 * WRITE A REVIEW pill (inside <wise-product-reviews>), moves the freshly
 * spawned WR <form> into our native <dialog>, and opens the modal.
 *
 * Selectors depended on (verified 2026-07-16 on lunched-v2 / dev store):
 *   Trigger:  <wise-product-reviews> button.wise-bg-slate-700 (text "WRITE A REVIEW")
 *   Form:     <wise-product-reviews> form  (only one exists inside the widget)
 *   Cancel:   form > button whose text is "Cancel" (with positional fallback)
 *
 * CLAUDE.md Section 14 forbids Custom Elements that directly query other
 * components by selector; the sanctioned alternative is DOM events. This
 * file is a documented exception: WiseReviews is a third-party app widget
 * with no event API to subscribe to, so directly querying and mutating
 * its DOM is the only option. The exception applies ONLY to third-party
 * app bridges - do NOT cite this file as precedent for first-party
 * component wiring.
 *
 * PDP-only. Fails silently on non-product templates or when <dialog> is
 * unsupported (iOS < 15.4).
 */
class PdpReviewsModal extends HTMLElement {
  constructor() {
    super();
    // Bound listener refs so disconnectedCallback can remove them.
    this._onWprClick     = this._onWprClick.bind(this);
    this._onCloseClick   = this._onCloseClick.bind(this);
    this._onDialogCancel = this._onDialogCancel.bind(this);
    // Observer + timer refs for teardown.
    this._wprObs    = null;
    this._wprTimer  = null;
    this._formObs   = null;
    this._formTimer = null;
    this._submitObs = null; // watches for WR removing the form (submit success)
    this._previewObs = null; // watches for WR-added media preview cells (inject X)
    this._successTimer = null; // auto-close timer for success state
    this._successMode = false; // true between submit-success and dialog.close
    this._labelObs = null; // keeps the trigger label as sentence case
    // Guards.
    this._bound   = false; // listener-attach idempotency
    this._closing = false; // re-entrancy around _close
  }

  connectedCallback() {
    // Belt-and-suspenders PDP scope check.
    if (!document.body.classList.contains('template-product')) return;

    // Feature-detect native <dialog>. iOS < 15.4 lacks it; there we simply
    // do not enhance and WR's inline form path continues to work.
    if (typeof HTMLDialogElement === 'undefined' ||
        typeof HTMLDialogElement.prototype.showModal !== 'function') return;

    this._dialog   = this.querySelector('[data-pdp-reviews-modal]');
    this._body     = this.querySelector('[data-pdp-reviews-modal-body]');
    this._closeBtn = this.querySelector('[data-pdp-reviews-modal-close]');
    if (!this._dialog || !this._body || !this._closeBtn) return;

    // Capture the pristine header copy so we can restore it after the
    // submit-success state (which mutates title + lede text) on close.
    const initialTitle = this._dialog.querySelector('.pdp-reviews-modal__title');
    const initialLede  = this._dialog.querySelector('.pdp-reviews-modal__lede');
    this._originalTitle = initialTitle ? initialTitle.textContent : '';
    this._originalLede  = initialLede  ? initialLede.textContent  : '';

    this._wpr = document.querySelector('wise-product-reviews');
    if (this._wpr) {
      this._bindListeners();
    } else {
      this._waitForWpr();
    }
  }

  disconnectedCallback() {
    // Remove listeners and disconnect any live observers/timers so the
    // instance can be garbage-collected. Required by CLAUDE.md Section 4.
    if (this._wpr) this._wpr.removeEventListener('click', this._onWprClick);
    if (this._closeBtn) this._closeBtn.removeEventListener('click', this._onCloseClick);
    if (this._dialog) {
      this._dialog.removeEventListener('cancel', this._onDialogCancel);
    }
    if (this._wprObs)    { this._wprObs.disconnect();     this._wprObs = null; }
    if (this._wprTimer)  { clearTimeout(this._wprTimer);  this._wprTimer = null; }
    if (this._formObs)   { this._formObs.disconnect();    this._formObs = null; }
    if (this._formTimer) { clearTimeout(this._formTimer); this._formTimer = null; }
    if (this._submitObs) { this._submitObs.disconnect();  this._submitObs = null; }
    if (this._previewObs){ this._previewObs.disconnect(); this._previewObs = null; }
    if (this._labelObs)  { this._labelObs.disconnect();   this._labelObs = null; }
    if (this._successTimer) { clearTimeout(this._successTimer); this._successTimer = null; }
    this._bound = false;
  }

  _waitForWpr() {
    // WR may render after our connectedCallback (both are defer-loaded).
    // Watch the body for the element to appear. setTimeout enforces the
    // 10s bound regardless of subsequent DOM activity (a callback-only
    // deadline check would leak forever on a page where WR never loads).
    this._wprObs = new MutationObserver(() => {
      const wpr = document.querySelector('wise-product-reviews');
      if (!wpr) return;
      this._wprObs.disconnect();
      clearTimeout(this._wprTimer);
      this._wprObs = null;
      this._wprTimer = null;
      this._wpr = wpr;
      this._bindListeners();
    });
    this._wprObs.observe(document.body, { childList: true, subtree: true });
    this._wprTimer = setTimeout(() => {
      if (!this._wprObs) return;
      this._wprObs.disconnect();
      this._wprObs = null;
      this._wprTimer = null;
      console.error('[pdp-reviews-modal] wpr-wait-timeout');
    }, 10000);
  }

  _bindListeners() {
    if (this._bound) return; // idempotency
    this._bound = true;
    this._wpr.addEventListener('click', this._onWprClick);
    this._closeBtn.addEventListener('click', this._onCloseClick);
    this._dialog.addEventListener('cancel', this._onDialogCancel);
    this._rewriteTriggerLabel();
  }

  _rewriteTriggerLabel() {
    // WR ships the trigger label as "WRITE A REVIEW". We rewrite to
    // sentence case so it matches the theme's "Build a set" CTA. A
    // one-shot rewrite isn't enough - WR re-renders the button on
    // various state changes (review count updates, variant swap,
    // review submission), and each re-render replaces the text node
    // with "WRITE A REVIEW" again. We watch the WR block for any
    // mutation and re-apply the label whenever it appears in uppercase.
    // The observer's own writes don't re-trigger: after we set
    // textContent to "Write a review", subsequent mutations find the
    // text already correct and the `if` bails.
    const doRelabel = () => {
      const trigger = this._wpr.querySelector('button.wise-bg-slate-700');
      if (!trigger) return;
      const t = (trigger.textContent || '').trim();
      if (t === 'WRITE A REVIEW') trigger.textContent = 'Write a review';
    };
    doRelabel();
    if (this._labelObs) this._labelObs.disconnect();
    this._labelObs = new MutationObserver(doRelabel);
    this._labelObs.observe(this._wpr, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  _onWprClick(e) {
    // Re-entrancy guard. _close() synthesizes cancelBtn.click(), which
    // bubbles back through this delegate. Bail out during close.
    if (this._closing) return;

    // Primary selector: exact class combo.
    let btn = e.target.closest('button.wise-bg-slate-700');
    // Fallback: match by text if WR ever renames the utility class.
    if (!btn) {
      const t = e.target.closest('button');
      if (t && (t.textContent || '').trim().toUpperCase() === 'WRITE A REVIEW') {
        btn = t;
      }
    }
    if (!btn) return;

    // Do NOT preventDefault / stopPropagation. WR's own click handler
    // needs to run to spawn the form. Microtask fires after this event's
    // dispatch task completes, at which point WR has spawned the form.
    queueMicrotask(() => this._captureFormAndOpen());
  }

  _onCloseClick() {
    this._close();
  }

  _onDialogCancel(e) {
    // Intercept ESC and backdrop cancel so our teardown runs before close.
    // The `cancel` event ALSO fires on <input type="file"> when the user
    // dismisses the OS file picker without selecting anything (Chrome
    // 113+). Because the input is inside our dialog, that cancel event
    // bubbles up here and would otherwise silently close the modal
    // mid-upload. Only act when the dialog itself is the origin target.
    if (e.target !== this._dialog) return;
    e.preventDefault();
    this._close();
  }

  _captureFormAndOpen() {
    const form = this._wpr.querySelector('form');
    if (form) {
      this._moveAndOpen(form);
      return;
    }
    // Async spawn fallback with a real timeout enforced by setTimeout.
    this._formObs = new MutationObserver(() => {
      const f = this._wpr.querySelector('form');
      if (!f) return;
      this._formObs.disconnect();
      clearTimeout(this._formTimer);
      this._formObs = null;
      this._formTimer = null;
      this._moveAndOpen(f);
    });
    this._formObs.observe(this._wpr, { childList: true, subtree: true });
    this._formTimer = setTimeout(() => {
      if (!this._formObs) return;
      this._formObs.disconnect();
      this._formObs = null;
      this._formTimer = null;
      console.error('[pdp-reviews-modal] form-spawn-timeout');
    }, 5000);
  }

  _moveAndOpen(form) {
    if (this._dialog.open) return; // guard against double-open
    this._body.appendChild(form);   // moves node; existing handlers stay attached
    this._upgradeBodyToTextarea(form);
    this._dialog.showModal();       // native backdrop + focus trap
    this._lockBodyScroll();         // showModal does NOT lock underlying scroll on iOS Safari
    const first = this._body.querySelector('input, textarea');
    if (first) first.focus();
    this._watchForSubmitSuccess();
    this._watchForMediaPreviews();
  }

  _lockBodyScroll() {
    // Lock via `touch-action: none` on <body> + `overflow: hidden` on
    // <html> and <body>. iOS Safari decides at TOUCHSTART whether a
    // gesture drives scroll based on the touched element's touch-action;
    // setting `none` on body blocks iOS from starting a scroll gesture
    // when the touch lands on body (i.e. outside the dialog). Descendant
    // elements (the dialog card) keep their default `touch-action: auto`
    // so their own scroll gestures work normally.
    //
    // NOT using position:fixed on body - that confuses iOS scroll-target
    // routing when the dialog card is the scroll container (user gets
    // stuck mid-modal unable to swipe back to the top).
    if (this._scrollLocked) return;
    this._prevHtmlOverflow    = document.documentElement.style.overflow;
    this._prevBodyOverflow    = document.body.style.overflow;
    this._prevBodyTouchAction = document.body.style.touchAction;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow    = 'hidden';
    document.body.style.touchAction = 'none';
    this._scrollLocked = true;
  }

  _unlockBodyScroll() {
    if (!this._scrollLocked) return;
    document.documentElement.style.overflow = this._prevHtmlOverflow    || '';
    document.body.style.overflow            = this._prevBodyOverflow    || '';
    document.body.style.touchAction         = this._prevBodyTouchAction || '';
    this._prevHtmlOverflow = null;
    this._prevBodyOverflow = null;
    this._prevBodyTouchAction = null;
    this._scrollLocked = false;
  }

  _watchForMediaPreviews() {
    // WR uploads each media file to their server on file-select (single
    // file per select event), then appends a preview cell into
    // `<div class="wise-flex wise-flex-wrap">` inside the media section.
    // WR ships no built-in remove UI, so we inject an X button on each
    // preview cell. Best-effort: click removes the preview DOM cell.
    // If WR's submit reads asset URLs from the DOM, the file is actually
    // removed from the submission; if WR maintains a JS-only state array,
    // only the visual is removed. The visual feedback still matches user
    // intent; the "may still submit" edge case is documented.
    if (this._previewObs) this._previewObs.disconnect();
    // Ensure any preview cells already present get an X (defensive - the
    // form was freshly moved in, so normally there aren't any yet).
    this._body.querySelectorAll('div[class~="wise-w-full"][class~="wise-p-1"]').forEach(
      (cell) => this._addRemoveButton(cell)
    );
    this._previewObs = new MutationObserver((records) => {
      for (const rec of records) {
        for (const node of rec.addedNodes) {
          if (!(node instanceof Element)) continue;
          // Direct match: new node IS a preview cell.
          if (node.matches && node.matches('div[class~="wise-w-full"][class~="wise-p-1"]')) {
            this._addRemoveButton(node);
          }
          // Nested match: new subtree contains preview cells.
          if (node.querySelectorAll) {
            node.querySelectorAll('div[class~="wise-w-full"][class~="wise-p-1"]').forEach(
              (cell) => this._addRemoveButton(cell)
            );
          }
        }
      }
    });
    this._previewObs.observe(this._body, { childList: true, subtree: true });
  }

  _addRemoveButton(cell) {
    if (cell.querySelector(':scope > .pdp-reviews-modal__media-remove')) return; // already added
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pdp-reviews-modal__media-remove';
    btn.setAttribute('aria-label', 'Remove media');
    btn.textContent = '×'; // multiplication sign, visually "x"
    btn.addEventListener('click', (e) => {
      e.stopPropagation(); // don't trigger the underlying role="button" preview click
      // Best-effort removal - see _watchForMediaPreviews comment.
      cell.remove();
    });
    cell.appendChild(btn);
    // Disable WR's lightbox trigger on the preview. WR renders its own
    // fullscreen viewer when the preview <div role="button"> is clicked,
    // but that viewer stacks BELOW our <dialog> (which is in the browser
    // top layer via showModal), so the user sees a hidden fullscreen behind
    // the modal. Cutting the click at the capture phase blocks WR's bubble-
    // phase handler from firing. Also removes ARIA/keyboard affordances so
    // the preview no longer advertises itself as interactive.
    const previewBtn = cell.querySelector('div[role="button"]');
    if (previewBtn && !previewBtn.dataset.pdpNoLightbox) {
      previewBtn.dataset.pdpNoLightbox = '1';
      previewBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
      }, { capture: true });
      previewBtn.removeAttribute('role');
      previewBtn.removeAttribute('tabindex');
      previewBtn.style.cursor = 'default';
    }
  }

  _watchForSubmitSuccess() {
    // On a successful submit, WR removes the form from the DOM and renders
    // its thank-you inside its original section (which is behind our
    // dialog backdrop). On mobile the viewport is often scrolled far above
    // the reviews block, so simply closing here would drop the user on
    // whatever section they were on before opening - they wouldn't see the
    // thank-you or their fresh review. Instead we swap in an in-modal
    // success state, hold it for a beat so the user visually registers it,
    // then close AND scroll to the reviews block so they land on the
    // updated list.
    if (this._submitObs) this._submitObs.disconnect();
    this._submitObs = new MutationObserver(() => {
      if (this._closing) return; // our own close is in progress
      if (this._body.querySelector('form')) return;
      this._submitObs.disconnect();
      this._submitObs = null;
      setTimeout(() => this._showSubmitSuccess(), 120);
    });
    this._submitObs.observe(this._body, { childList: true });
  }

  _showSubmitSuccess() {
    if (!this._dialog.open) return; // user already closed
    this._successMode = true;
    // Rewrite the header for the success state
    const title = this._dialog.querySelector('.pdp-reviews-modal__title');
    const lede  = this._dialog.querySelector('.pdp-reviews-modal__lede');
    if (title) title.textContent = 'Review submitted';
    if (lede)  lede.textContent = 'Thanks for sharing your experience.';
    // Swap the body for a success note. The rewritten header already
    // announces "Review submitted"/"Thanks for sharing your experience.";
    // this paragraph adds the practical detail.
    this._body.innerHTML =
      '<p class="pdp-reviews-modal__success-note">Your review has been received. You can close this window - your feedback will appear in the reviews list below shortly.</p>';
    // Auto-close after 15s so the user has plenty of time to read the
    // confirmation - especially on mobile where the modal is often
    // covering their viewport in full. User can still hit X (or ESC) to
    // close earlier.
    if (this._successTimer) clearTimeout(this._successTimer);
    this._successTimer = setTimeout(() => {
      this._close({ syncWr: false, scrollToReviews: true });
    }, 15000);
  }

  _upgradeBodyToTextarea(form) {
    // WR renders the Review Body as <input type="text" name="body"> which
    // gives a single-line field. We want a real multi-line textarea.
    // We CANNOT replace the input outright: WR's client framework holds a
    // reference to the original input node captured at form-spawn time,
    // and it reads `.value` from that reference at submit. If we
    // replaceWith(), the reference goes stale and WR submits an empty
    // body -> the server 302s to an error page -> the browser blocks
    // the redirect on CORS.
    // Instead: visually hide the original input (leaving it in the DOM
    // so WR's reference stays valid), and insert a sibling <textarea>
    // that mirrors its value on every keystroke. Dispatching `input`
    // and `change` on the original propagates the value through
    // whichever reactive binding WR uses.
    const inp = form.querySelector('input[type="text"][name="body"]');
    if (!inp || inp.dataset.pdpUpgraded === '1') return;
    inp.dataset.pdpUpgraded = '1';
    // Visually-hide the original (offscreen). aria-hidden + tabindex=-1
    // so screen readers and keyboard focus skip it.
    inp.style.position = 'absolute';
    inp.style.left = '-10000px';
    inp.style.width = '1px';
    inp.style.height = '1px';
    inp.style.opacity = '0';
    inp.setAttribute('tabindex', '-1');
    inp.setAttribute('aria-hidden', 'true');
    const ta = document.createElement('textarea');
    ta.setAttribute('data-pdp-body-mirror', '');
    ta.rows = 4;
    if (inp.placeholder) ta.placeholder = inp.placeholder;
    if (inp.value) ta.value = inp.value;
    if (inp.required) ta.required = true;
    // If the underlying <input> is labelled (label[for=name-of-input]),
    // point the label at the textarea for a11y - since the input is
    // effectively invisible, screen readers should read the textarea.
    if (inp.id) {
      ta.id = inp.id + '-mirror';
      const lbl = form.querySelector('label[for="' + inp.id + '"]');
      if (lbl) lbl.setAttribute('for', ta.id);
    }
    const sync = (eventType) => {
      inp.value = ta.value;
      inp.dispatchEvent(new Event(eventType, { bubbles: true }));
    };
    ta.addEventListener('input', () => sync('input'));
    ta.addEventListener('change', () => sync('change'));
    // Insert textarea in place of the input in the visual flow. The
    // input stays as a sibling for WR's reference to remain valid.
    inp.parentNode.insertBefore(ta, inp);
  }

  _close(opts) {
    if (this._closing) return; // guard against re-entrant close paths
    this._closing = true;
    // When we're in success mode (WR already tore down its form), never
    // sync back to WR - clicking its trigger would open a fresh form the
    // user didn't ask for.
    const syncWr = this._successMode
      ? false
      : (opts ? opts.syncWr !== false : true);
    const scrollToReviews = (opts && opts.scrollToReviews === true) || this._successMode;
    try {
      if (this._submitObs)  { this._submitObs.disconnect();  this._submitObs = null; }
      if (this._previewObs) { this._previewObs.disconnect(); this._previewObs = null; }
      if (this._successTimer) { clearTimeout(this._successTimer); this._successTimer = null; }
      // WR's write-review flow has no Cancel button - WRITE A REVIEW is a
      // TOGGLE: click once to spawn the form, click again to remove it. To
      // sync WR's state to "closed" (so the next real user click gives us
      // a fresh spawn in one click, not two), we programmatically click WR's
      // own trigger. But first we move any form still in our body back to
      // WR's section so WR's toggle-close removes it from the location it
      // expects. Our _onWprClick short-circuits on this._closing so the
      // synth click won't reopen the dialog.
      //
      // syncWr=false path: submit-success close. WR already reset its own
      // state during submit; clicking the trigger here would toggle back
      // to "open" and spawn a fresh form the user didn't ask for.
      const form = this._body.querySelector('form');
      if (form && this._wpr) {
        const wrSection = this._wpr.querySelector('section.wise-product-reviews');
        if (wrSection) wrSection.appendChild(form);
      }
      if (syncWr && this._wpr) {
        const trigger = this._wpr.querySelector('button.wise-bg-slate-700');
        if (trigger) trigger.click();
      }
      // Reset the modal chrome to its pristine state. Any residual
      // success card injected during _showSubmitSuccess gets cleared, and
      // the header title/lede are restored to the values captured on
      // connectedCallback so the next open starts clean.
      this._body.innerHTML = '';
      const title = this._dialog.querySelector('.pdp-reviews-modal__title');
      const lede  = this._dialog.querySelector('.pdp-reviews-modal__lede');
      if (title) title.textContent = this._originalTitle;
      if (lede)  lede.textContent  = this._originalLede;
      if (this._dialog.open) this._dialog.close();
      this._unlockBodyScroll();
      if (scrollToReviews && this._wpr) {
        // Land the user on the (now updated) reviews list. Timeout gives
        // the dialog close a beat so the scroll doesn't fight it.
        setTimeout(() => {
          try { this._wpr.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
          catch (_) { this._wpr.scrollIntoView(); }
        }, 100);
      }
    } finally {
      this._closing = false;
      this._successMode = false;
    }
  }
}

customElements.define('pdp-reviews-modal', PdpReviewsModal);
